import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  SportsTennis as SportsIcon,
  PictureAsPdf as PdfIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

interface AcademyReportsPageProps {
  academyId: string;
}

interface ReportRecord {
  id: string;
  playerId: string;
  playerName: string;
  playerIdCode: string;
  sport: string;
  reportType: 'training' | 'ranking';
  category?: string;
  assessmentType?: string;
  period: string;
  dateRange?: { start: string; end: string };
  submittedAt: string;
  averageRating?: number;
            score?: number;
  grade?: string;
  totalStrokes?: number;
  coachId?: string | null;
  coachName?: string;
  submittedBy: 'coach' | 'admin' | 'self';
}

const AcademyReportsPage: React.FC<AcademyReportsPageProps> = ({ academyId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine view type from URL
  const viewType = location.pathname.includes('/sport-wise') ? 'sport-wise' : 'player-wise';
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<ReportRecord[]>([]);
  const [players, setPlayers] = useState<any[]>([]);
  
  // Filters
  const [selectedSport, setSelectedSport] = useState<string>('all');
  const [selectedPeriodType, setSelectedPeriodType] = useState<string>('all'); // 'all', 'yearly', 'quarterly', 'monthly'
  const [selectedPeriod, setSelectedPeriod] = useState<string>('all'); // Specific period like 'Y-2025', 'Q1-2025', 'month-1'
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedReportType, setSelectedReportType] = useState<string>('all');
  const [selectedSubmittedBy, setSelectedSubmittedBy] = useState<string>('all');
  const [selectedPlayer, setSelectedPlayer] = useState<string>('all');

  useEffect(() => {
    loadPlayers();
  }, [academyId]);

  useEffect(() => {
    loadReports();
  }, [academyId, viewType, selectedSport, selectedPeriodType, selectedPeriod, selectedYear, selectedReportType, selectedSubmittedBy, selectedPlayer]);

  const loadPlayers = async () => {
    try {
      const response = await fetch(`/api/academies/${academyId}/players`);
      const result = await response.json();
      if (result.success) {
        setPlayers(result.data || []);
      }
    } catch (err) {
      console.error('Error loading players:', err);
    }
  };

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedSport !== 'all') params.set('sport', selectedSport);
      if (selectedPeriodType !== 'all' && selectedPeriod !== 'all' && selectedPeriod) {
        params.set('periodKey', selectedPeriod);
      }
      if (selectedReportType !== 'all') {
        // For now, we'll load both types and filter client-side
      }
      if (selectedSubmittedBy !== 'all') params.set('submittedBy', selectedSubmittedBy);
      if (viewType === 'player-wise' && selectedPlayer !== 'all') {
        params.set('playerId', selectedPlayer);
      }
      
      // Load training reports
      const trainingRes = await fetch(`/api/academies/${academyId}/reports/training?${params.toString()}`);
      const trainingData = await trainingRes.json();
      
      // Load ranking reports
      const rankingRes = await fetch(`/api/academies/${academyId}/reports/ranking?${params.toString()}`);
      const rankingData = await rankingRes.json();
      
      const allReports: ReportRecord[] = [];
      
      if (trainingData.success) {
        trainingData.data.forEach((report: any) => {
          allReports.push({
            id: report.id,
            playerId: report.playerId,
            playerName: report.playerName,
            playerIdCode: report.playerIdCode,
            sport: report.sport,
            reportType: 'training',
            category: report.category,
            assessmentType: report.assessmentType,
            period: report.period,
            dateRange: report.dateRange,
            submittedAt: report.submittedAt,
            averageRating: report.averageRating,
            coachId: report.coachId,
            coachName: report.submittedBy?.coachName,
            submittedBy: report.submittedBy?.type || 'self'
          });
        });
      }
      
      if (rankingData.success) {
        rankingData.data.forEach((report: any) => {
          allReports.push({
            id: report.reportId,
            playerId: report.playerId,
            playerName: report.playerName,
            playerIdCode: report.playerIdCode,
            sport: report.sport,
            reportType: 'ranking',
            category: report.categoryId,
            period: report.periodKey,
            dateRange: { start: report.periodEndDate, end: report.periodEndDate },
            submittedAt: report.submittedAt,
            score: report.score,
            grade: report.grade,
            totalStrokes: report.totalStrokes,
            coachId: report.submittedBy?.coachId,
            coachName: report.submittedBy?.coachName,
            submittedBy: report.submittedBy?.type || 'player'
          });
        });
      }
      
      // Filter by report type if needed
      let filtered = allReports;
      if (selectedReportType !== 'all') {
        filtered = allReports.filter(r => r.reportType === selectedReportType);
      }
      
      setReports(filtered);
    } catch (err: any) {
      console.error('Error loading reports:', err);
      setError('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const filteredReports = reports.filter(report => {
    if (selectedSport !== 'all' && report.sport !== selectedSport) return false;
    if (selectedReportType !== 'all' && report.reportType !== selectedReportType) return false;
    if (selectedSubmittedBy !== 'all' && report.submittedBy !== selectedSubmittedBy) return false;
    if (viewType === 'player-wise' && selectedPlayer !== 'all' && report.playerId !== selectedPlayer) return false;
    
    // Filter by period
    if (selectedPeriodType !== 'all' && selectedPeriod !== 'all') {
      if (report.reportType === 'training') {
        // Training reports use period like "yearly", "quarter-1", "month-1"
        const reportPeriod = (report.period || '').toLowerCase();
        if (selectedPeriodType === 'yearly') {
          if (!reportPeriod.includes('yearly')) return false;
        } else if (selectedPeriodType === 'quarterly') {
          if (!reportPeriod.includes('quarter')) return false;
          if (selectedPeriod !== 'all') {
            const quarter = selectedPeriod.match(/Q(\d)/)?.[1];
            if (quarter && !reportPeriod.includes(`quarter-${quarter}`)) return false;
          }
        } else if (selectedPeriodType === 'monthly') {
          if (!reportPeriod.includes('month')) return false;
          if (selectedPeriod !== 'all') {
            const month = selectedPeriod.match(/month-(\d+)/)?.[1];
            if (month && !reportPeriod.includes(`month-${month}`)) return false;
          }
        }
      } else if (report.reportType === 'ranking') {
        // Ranking reports use periodKey like "Y-2025", "Q1-2025"
        const reportPeriod = report.period || '';
        if (selectedPeriodType === 'yearly') {
          if (!reportPeriod.startsWith('Y-')) return false;
        } else if (selectedPeriodType === 'quarterly') {
          if (!reportPeriod.startsWith('Q')) return false;
          if (selectedPeriod !== 'all' && reportPeriod !== selectedPeriod) return false;
        }
      }
    }
    
    return true;
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {viewType === 'player-wise' ? 'Player-wise Reports' : 'Sport-wise Reports'}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
        View training reports evaluated by coaches and ranking reports (practical tests) submitted by EncycGames admin. Self-evaluations are not shown.
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sport</InputLabel>
              <Select value={selectedSport} onChange={(e) => setSelectedSport(e.target.value)} label="Sport">
                <MenuItem value="all">All Sports</MenuItem>
                <MenuItem value="Badminton">Badminton</MenuItem>
                <MenuItem value="Cricket">Cricket</MenuItem>
                <MenuItem value="Tennis">Tennis</MenuItem>
                <MenuItem value="Football">Football</MenuItem>
                <MenuItem value="Athletics">Athletics</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Report Type</InputLabel>
              <Select value={selectedReportType} onChange={(e) => setSelectedReportType(e.target.value)} label="Report Type">
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="training">Training</MenuItem>
                <MenuItem value="ranking">Ranking</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Submitted By</InputLabel>
              <Select value={selectedSubmittedBy} onChange={(e) => setSelectedSubmittedBy(e.target.value)} label="Submitted By">
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="coach">Coach</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Period Type</InputLabel>
              <Select 
                value={selectedPeriodType} 
                onChange={(e) => {
                  setSelectedPeriodType(e.target.value);
                  setSelectedPeriod('all');
                }} 
                label="Period Type"
              >
                <MenuItem value="all">All Periods</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {selectedPeriodType !== 'all' && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select 
                  value={selectedYear} 
                  onChange={(e) => {
                    setSelectedYear(Number(e.target.value));
                    setSelectedPeriod('all');
                  }} 
                  label="Year"
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          )}
          {selectedPeriodType !== 'all' && selectedPeriodType !== 'yearly' && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>
                  {selectedPeriodType === 'quarterly' ? 'Quarter' : 'Month'}
                </InputLabel>
                <Select 
                  value={selectedPeriod} 
                  onChange={(e) => setSelectedPeriod(e.target.value)} 
                  label={selectedPeriodType === 'quarterly' ? 'Quarter' : 'Month'}
                >
                  <MenuItem value="all">All</MenuItem>
                  {selectedPeriodType === 'quarterly' ? (
                    <>
                      <MenuItem value={`Q1-${selectedYear}`}>Q1 {selectedYear}</MenuItem>
                      <MenuItem value={`Q2-${selectedYear}`}>Q2 {selectedYear}</MenuItem>
                      <MenuItem value={`Q3-${selectedYear}`}>Q3 {selectedYear}</MenuItem>
                      <MenuItem value={`Q4-${selectedYear}`}>Q4 {selectedYear}</MenuItem>
                    </>
                  ) : (
                    Array.from({ length: 12 }, (_, i) => {
                      const monthNum = i + 1;
                      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                      return (
                        <MenuItem key={monthNum} value={`month-${monthNum}`}>
                          {monthNames[i]} {selectedYear}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}
          {selectedPeriodType === 'yearly' && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Year Period</InputLabel>
                <Select 
                  value={selectedPeriod} 
                  onChange={(e) => setSelectedPeriod(e.target.value)} 
                  label="Year Period"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value={`Y-${selectedYear}`}>Yearly {selectedYear}</MenuItem>
                  <MenuItem value="yearly">Yearly (Generic)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          {viewType === 'player-wise' && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Player</InputLabel>
                <Select value={selectedPlayer} onChange={(e) => setSelectedPlayer(e.target.value)} label="Player">
                  <MenuItem value="all">All Players</MenuItem>
                  {players.map((player) => (
                    <MenuItem key={player.id} value={player.id}>
                      {player.firstName} {player.lastName} ({player.playerIdCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredReports.length === 0 ? (
        <Alert severity="info">
          {reports.length === 0 
            ? 'No reports available. Reports will appear here once coaches or admins submit evaluations.'
            : 'No reports match the selected filters.'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.light' }}>
                <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sport</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Report Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Assessment/Details</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Submitted Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Evaluated By</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Score/Rating</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReports.map((report) => (
                <TableRow key={report.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {report.playerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {report.playerIdCode}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={report.sport} color="primary" size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={report.reportType === 'training' ? 'Training' : 'Ranking'} 
                      color={report.reportType === 'training' ? 'info' : 'secondary'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    {report.reportType === 'training' ? (
                      <Chip
                        label={report.assessmentType === 'high-level' ? 'High-level' : report.assessmentType === 'low-level' ? 'Low-level' : 'N/A'}
                        color={report.assessmentType === 'high-level' ? 'primary' : 'secondary'}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {report.sport === 'Badminton' ? 'Strokes' : report.sport === 'Cricket' ? 'Shots' : 'Items'}: {report.totalStrokes || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.category === 'coach-academy' ? 'Coach/Academy' : report.category === 'practical-test' ? 'Board Exam' : 'N/A'}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{report.period}</TableCell>
                  <TableCell>
                    {report.submittedAt ? new Date(report.submittedAt).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {report.coachName || (report.submittedBy === 'admin' ? 'EncycGames Admin' : 'N/A')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={report.coachId ? 'Coach Evaluation' : report.submittedBy === 'admin' ? 'Admin Evaluation' : 'N/A'}
                      color={report.coachId ? 'success' : report.submittedBy === 'admin' ? 'warning' : 'default'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    {report.reportType === 'training' ? (
                      <Chip 
                        label={report.averageRating?.toFixed(1) || 'N/A'} 
                        color={report.averageRating && report.averageRating >= 4 ? 'success' : report.averageRating && report.averageRating >= 3 ? 'warning' : 'error'}
                        size="small"
                      />
                    ) : (
                      <Box>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {report.grade || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.score || 0} pts
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => {
                        // TODO: Navigate to report details
                        console.log('View report:', report.id);
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AcademyReportsPage;

